import React, { useState } from 'react';
import { Home, Plus, Trash2, Clock, Save, X, ChevronLeft, ChevronRight, Calendar, MapPin, Sparkles } from 'lucide-react';
import useLocalStorage from './hooks/useLocalStorage';
import { formatTime, getCategoryStyle } from './utils';

const MAX_TEXT  = 100;
const MAX_NOTES = 500;

const sanitize = (str, max = MAX_TEXT) => String(str ?? '').replace(/[<>"'`]/g, '').slice(0, max);

const DAY_NAMES  = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const CATEGORIES = ['class', 'work', 'exercise', 'personal', 'other'];
const HOURS      = Array.from({ length: 16 }, (_, i) => i + 6);

const newEventTemplate = (day) => ({
  id:         Date.now(),
  title:      '',
  day,
  startTime:  '09:00',
  endTime:    '10:00',
  category:   'class',
  location:   '',
  notes:      '',
  repeatDays: [],
});

// ── Timetable ─────────────────────────────────────────────────

const Timetable = ({ setCurrentModule }) => {
  const [events, setEvents] = useLocalStorage('timetableEvents', []);
  const [editingEvent,    setEditingEvent]    = useState(null);
  const [showEventModal,  setShowEventModal]  = useState(false);
  const [currentDay,      setCurrentDay]      = useState(new Date().getDay());
  const [view,            setView]            = useState('today');

  const today = new Date().getDay();

  const openNew  = (day = currentDay) => { setEditingEvent(newEventTemplate(day)); setShowEventModal(true); };
  const openEdit = (event) => { setEditingEvent(event); setShowEventModal(true); };
  const closeModal = () => { setShowEventModal(false); setEditingEvent(null); };

  const saveEvent = () => {
    const title = sanitize(editingEvent.title);
    if (!title.trim()) return;
    const clean = {
      ...editingEvent,
      title,
      location: sanitize(editingEvent.location),
      notes:    sanitize(editingEvent.notes, MAX_NOTES),
    };
    setEvents(events.find(e => e.id === clean.id)
      ? events.map(e => e.id === clean.id ? clean : e)
      : [...events, clean]
    );
    closeModal();
  };

  const deleteEvent = (id) => {
    setEvents(events.filter(e => e.id !== id));
    closeModal();
  };

  const getEventsForDay = (dayIdx) =>
    events
      .filter(e => e.repeatDays?.length > 0 ? e.repeatDays.includes(dayIdx) : e.day === dayIdx)
      .sort((a, b) => a.startTime.localeCompare(b.startTime));

  const todayEvents = getEventsForDay(currentDay);

  return (
    <div className="page">
      <div className="page-content">
        <button onClick={() => setCurrentModule('dashboard')} className="btn-back">
          <Home className="w-4 h-4" /><span>Dashboard</span>
        </button>

        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <h1 className="page-title">Timetable</h1>
            <Clock className="w-6 h-6 text-neutral-900" />
          </div>
          <p className="page-subtitle">Weekly Schedule</p>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <button onClick={() => setView(v => v === 'today' ? 'week' : 'today')} className="flex items-center justify-center gap-2 border-2 border-neutral-900 bg-white text-neutral-900 px-4 py-3 text-sm uppercase tracking-wider font-bold hover-lift font-mono">
            <Calendar className="w-4 h-4" />
            {view === 'today' ? 'Week View' : 'Day View'}
          </button>
          <button onClick={() => openNew()} className="flex items-center justify-center gap-2 bg-neutral-900 text-white px-4 py-3 text-sm uppercase tracking-wider font-bold border-2 border-neutral-900 hover:bg-neutral-700 transition-all shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover-lift-lg font-mono">
            <Plus className="w-4 h-4" /> Add Event
          </button>
        </div>

        {view === 'today' ? (
          <DayView
            events={todayEvents}
            currentDay={currentDay}
            today={today}
            onPrev={() => setCurrentDay(d => (d - 1 + 7) % 7)}
            onNext={() => setCurrentDay(d => (d + 1) % 7)}
            onToday={() => setCurrentDay(today)}
            onEdit={openEdit}
            onAdd={openNew}
          />
        ) : (
          <WeekView events={events} today={today} getEventsForDay={getEventsForDay} onDayClick={(d) => { setCurrentDay(d); setView('today'); }} onEdit={openEdit} />
        )}

        {/* Legend */}
        <div className="card-md bg-white p-4 sm:p-6 mt-6">
          <div className="flex items-center gap-2 mb-4 border-b-2 border-neutral-900 pb-3">
            <Sparkles className="w-5 h-5 text-neutral-900" />
            <h3 className="text-sm uppercase tracking-wider text-neutral-900 font-bold font-mono">Categories</h3>
          </div>
          <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-3">
            {CATEGORIES.map(cat => (
              <div key={cat} className="flex items-center gap-2 border-2 border-neutral-900 px-3 py-2 bg-white hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] hover:-translate-x-0.5 hover:-translate-y-0.5 transition-all">
                <div className={`w-6 h-6 border-2 border-neutral-900 ${getCategoryStyle(cat)}`} />
                <span className="text-sm capitalize font-mono font-bold text-neutral-900 uppercase">{cat}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {showEventModal && editingEvent && (
        <EventModal
          event={editingEvent}
          setEvent={setEditingEvent}
          isEditing={!!events.find(e => e.id === editingEvent.id)}
          onSave={saveEvent}
          onDelete={deleteEvent}
          onCancel={closeModal}
        />
      )}
    </div>
  );
};

// ── DayView ───────────────────────────────────────────────────

const DayView = ({ events, currentDay, today, onPrev, onNext, onToday, onEdit, onAdd }) => (
  <div className="card-md bg-white mb-6">
    <div className="card-header sm:p-6 bg-neutral-900">
      <div className="flex items-center justify-between">
        <button onClick={onPrev} className="p-2 border-2 border-white hover:bg-white hover:text-neutral-900 transition-all text-white"><ChevronLeft className="w-5 h-5" /></button>
        <div className="text-center">
          <h2 className="text-2xl sm:text-3xl font-serif text-white uppercase">{DAY_NAMES[currentDay]}</h2>
          {currentDay === today && <p className="text-sm text-neutral-300 font-mono font-bold uppercase tracking-wider">Today</p>}
        </div>
        <button onClick={onNext} className="p-2 border-2 border-white hover:bg-white hover:text-neutral-900 transition-all text-white"><ChevronRight className="w-5 h-5" /></button>
      </div>
      {currentDay !== today && (
        <div className="mt-4">
          <button onClick={onToday} className="w-full border-2 border-white text-white px-4 py-2 text-sm hover:bg-white hover:text-neutral-900 transition-all font-mono font-bold uppercase">
            → Go to Today
          </button>
        </div>
      )}
    </div>

    <div className="p-4 sm:p-6">
      {events.length === 0 ? (
        <div className="text-center py-12 border-4 border-dashed border-neutral-900">
          <Clock className="w-20 h-20 text-neutral-900 mx-auto mb-4 border-4 border-neutral-900 p-3" />
          <p className="text-neutral-700 mb-6 font-mono uppercase tracking-wide">No events for {DAY_NAMES[currentDay]}</p>
          <button onClick={() => onAdd(currentDay)} className="bg-neutral-900 text-white px-6 py-3 text-sm uppercase tracking-wider font-bold hover:bg-neutral-700 transition-all card-sm hover-lift-lg font-mono">
            Add Event
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {events.map(event => (
            <div
              key={event.id}
              className={`border-4 p-4 sm:p-6 cursor-pointer hover-lift-lg border-neutral-900 ${getCategoryStyle(event.category)}`}
              onClick={() => onEdit(event)}
            >
              <div className="flex flex-col sm:flex-row sm:items-start justify-between mb-3 gap-3">
                <div className="flex-1">
                  <h3 className="text-xl sm:text-2xl font-serif mb-3 text-neutral-900 font-bold uppercase">{event.title}</h3>
                  <div className="flex items-center gap-2 text-sm mb-2 border-2 border-neutral-900 bg-white px-3 py-2 inline-flex font-mono font-bold">
                    <Clock className="w-4 h-4" />
                    <span>{formatTime(event.startTime)} - {formatTime(event.endTime)}</span>
                  </div>
                  {event.location && (
                    <div className="text-sm mt-3 flex items-center gap-2 font-mono">
                      <MapPin className="w-4 h-4" />{event.location}
                    </div>
                  )}
                </div>
                <span className="text-xs uppercase tracking-wider px-3 py-2 border-2 border-neutral-900 bg-white font-mono font-bold flex-shrink-0">
                  {event.category}
                </span>
              </div>
              {event.notes && <div className="text-sm pt-3 border-t-2 border-neutral-900 font-mono text-neutral-700">{event.notes}</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  </div>
);

// ── WeekView ──────────────────────────────────────────────────

const WeekView = ({ today, getEventsForDay, onDayClick, onEdit }) => (
  <div className="card-md bg-white overflow-x-auto">
    <div className="min-w-[1000px]">
      <div className="grid grid-cols-8 border-b-2 border-neutral-900">
        <div className="p-4 bg-neutral-900 border-r-2 border-neutral-900">
          <span className="text-xs uppercase tracking-wider text-white font-mono font-bold">Time</span>
        </div>
        {DAY_NAMES.map((day, idx) => (
          <div key={idx} className={`p-4 text-center border-r-2 border-neutral-900 ${idx === today ? 'bg-yellow-300' : 'bg-neutral-900'}`}>
            <div className={`text-xs uppercase tracking-wider font-mono font-bold ${idx === today ? 'text-neutral-900' : 'text-neutral-400'}`}>{day.slice(0,3)}</div>
            <div className={`text-sm font-bold mt-1 font-mono ${idx === today ? 'text-neutral-900' : 'text-white'}`}>{day}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-8">
        <div className="border-r-2 border-neutral-900">
          {HOURS.map(hour => (
            <div key={hour} className="h-20 border-b-2 border-neutral-900 px-3 py-2 text-right bg-neutral-900">
              <span className="text-xs text-white font-mono font-bold">
                {hour < 12 ? `${hour} AM` : hour === 12 ? '12 PM' : `${hour - 12} PM`}
              </span>
            </div>
          ))}
        </div>

        {[0,1,2,3,4,5,6].map(dayIdx => {
          const dayEvents = getEventsForDay(dayIdx);
          return (
            <div key={dayIdx} className="border-r-2 border-neutral-900 relative">
              {HOURS.map((_, i) => (
                <div key={i} className="h-20 border-b-2 border-neutral-900 hover:bg-neutral-100 transition-colors cursor-pointer" onClick={() => onDayClick(dayIdx)} />
              ))}
              <div className="absolute inset-0 pointer-events-none">
                {dayEvents.map(event => {
                  const [sh, sm] = event.startTime.split(':').map(Number);
                  const [eh, em] = event.endTime.split(':').map(Number);
                  const top    = ((sh - 6) * 60 + sm) / 60 * 80;
                  const height = ((eh - 6) * 60 + em) / 60 * 80 - top;
                  if (sh < 6 || sh >= 22) return null;
                  return (
                    <div
                      key={event.id}
                      className={`absolute left-1 right-1 border-2 border-neutral-900 ${height < 60 ? 'p-1' : 'p-2'} pointer-events-auto cursor-pointer hover-lift text-xs overflow-hidden ${getCategoryStyle(event.category)}`}
                      style={{ top: `${top}px`, height: `${Math.max(height, 50)}px` }}
                      onClick={e => { e.stopPropagation(); onEdit(event); }}
                    >
                      <div className="font-bold truncate font-mono uppercase text-neutral-900">{event.title}</div>
                      <div className="mt-1 font-mono font-bold text-neutral-700 text-xs">{formatTime(event.startTime)}</div>
                      {event.location && (
                        <div className="truncate text-xs font-mono text-neutral-600 flex items-center gap-1 mt-1">
                          <MapPin className="w-3 h-3" />{event.location}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  </div>
);

// ── EventModal ────────────────────────────────────────────────

const EventModal = ({ event, setEvent, isEditing, onSave, onDelete, onCancel }) => (
  <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center p-4 z-50">
    <div className="bg-white border-4 border-neutral-900 max-w-md w-full shadow-[12px_12px_0px_0px_rgba(0,0,0,1)] max-h-[90vh] overflow-y-auto">
      <div className="border-b-4 border-neutral-900 p-4 sm:p-6 flex items-center justify-between bg-neutral-900 sticky top-0">
        <h2 className="modal-title">{isEditing ? 'Edit Event' : 'New Event'}</h2>
        <button onClick={onCancel} className="text-white hover:bg-neutral-700 transition-colors border-2 border-white p-1"><X className="w-5 h-5" /></button>
      </div>

      <div className="p-4 sm:p-6 space-y-4">
        <div>
          <label className="label mb-2">Event Title *</label>
          <input
            type="text" value={event.title} maxLength={MAX_TEXT} autoFocus
            onChange={e => setEvent({ ...event, title: e.target.value })}
            placeholder="CHEMISTRY 101, GYM SESSION"
            className="w-full border-4 border-neutral-900 bg-white px-3 py-3 text-neutral-900 placeholder-neutral-400 focus:outline-none focus:shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] transition-all font-mono font-bold uppercase"
          />
        </div>

        <div>
          <label className="label mb-3">Repeat On</label>
          <div className="grid grid-cols-4 sm:grid-cols-7 gap-2">
            {DAY_NAMES.map((name, idx) => {
              const selected = event.repeatDays?.includes(idx);
              return (
                <button
                  key={idx} type="button"
                  onClick={() => {
                    const cur = event.repeatDays || [];
                    setEvent({ ...event, repeatDays: selected ? cur.filter(d => d !== idx) : [...cur, idx].sort((a,b) => a-b) });
                  }}
                  className={`p-3 border-2 text-center transition-all hover:-translate-x-0.5 hover:-translate-y-0.5 border-neutral-900 ${selected ? 'bg-neutral-900 text-white shadow-[2px_2px_0px_0px_rgba(0,0,0,1)]' : 'bg-white text-neutral-900 hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)]'}`}
                >
                  <span className="text-xs sm:text-sm font-bold font-mono uppercase">{name.slice(0,3)}</span>
                </button>
              );
            })}
          </div>
          <p className="text-xs text-neutral-600 mt-2 font-mono">
            {event.repeatDays?.length > 0 ? `Repeats on ${event.repeatDays.length} day${event.repeatDays.length > 1 ? 's' : ''}` : 'Select days to repeat this event'}
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="label mb-2">Start Time</label>
            <input type="time" value={event.startTime} onChange={e => setEvent({ ...event, startTime: e.target.value })} className="w-full input" />
          </div>
          <div>
            <label className="label mb-2">End Time</label>
            <input type="time" value={event.endTime} onChange={e => setEvent({ ...event, endTime: e.target.value })} className="w-full input" />
          </div>
        </div>

        <div>
          <label className="label mb-3">Category</label>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {CATEGORIES.map(cat => (
              <button
                key={cat}
                onClick={() => setEvent({ ...event, category: cat })}
                className={`p-3 border-2 text-left transition-all hover:-translate-x-0.5 hover:-translate-y-0.5 border-neutral-900 ${event.category === cat ? `${getCategoryStyle(cat)} shadow-[2px_2px_0px_0px_rgba(0,0,0,1)]` : 'bg-white hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)]'}`}
              >
                <span className="text-sm font-bold capitalize font-mono uppercase text-neutral-900">{cat}</span>
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="label mb-2">Location</label>
          <input
            type="text" value={event.location} maxLength={MAX_TEXT}
            onChange={e => setEvent({ ...event, location: e.target.value })}
            placeholder="ROOM 203, MAIN GYM"
            className="w-full border-4 border-neutral-900 bg-white px-3 py-3 text-neutral-900 placeholder-neutral-400 focus:outline-none focus:shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] transition-all font-mono font-bold uppercase"
          />
        </div>

        <div>
          <label className="label mb-2">Notes</label>
          <textarea
            value={event.notes} maxLength={MAX_NOTES}
            onChange={e => setEvent({ ...event, notes: e.target.value })}
            placeholder="Materials needed, reminders, etc."
            rows={3}
            className="w-full border-4 border-neutral-900 bg-white px-3 py-3 text-sm text-neutral-900 placeholder-neutral-400 focus:outline-none focus:shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] transition-all font-mono"
          />
        </div>
      </div>

      <div className="border-t-4 border-neutral-900 p-4 sm:p-6 flex flex-col-reverse sm:flex-row gap-3 sticky bottom-0 bg-white">
        {isEditing && (
          <button onClick={() => onDelete(event.id)} className="border-2 border-neutral-900 bg-white text-neutral-900 px-4 py-3 text-sm uppercase tracking-wider font-bold hover:bg-red-600 hover:text-white hover:border-red-600 transition-all flex items-center justify-center gap-2 w-full sm:w-auto font-mono">
            <Trash2 className="w-4 h-4" /> Delete
          </button>
        )}
        <div className="flex-1 flex flex-col-reverse sm:flex-row gap-3">
          <button onClick={onCancel} className="flex-1 border-2 border-neutral-900 bg-white text-neutral-900 py-3 text-sm uppercase tracking-wider font-bold hover-lift font-mono">Cancel</button>
          <button
            onClick={onSave}
            disabled={!event.title.trim()}
            className="flex-1 bg-neutral-900 text-white py-3 text-sm uppercase tracking-wider font-bold hover:bg-neutral-700 transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed card-sm hover-lift-lg disabled:hover:shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] disabled:hover:translate-x-0 disabled:hover:translate-y-0 font-mono"
          >
            <Save className="w-4 h-4" /> Save
          </button>
        </div>
      </div>
    </div>
  </div>
);

export default Timetable;